import React, { useState, useEffect } from 'lib/react';
import {
  EVPaper, EVTypography, EVGrid, EVTabs, EVTab, EVSkeleton,
  EVTable, EVTableBody, EVTableRow, EVTableCell, EVTableContainer,
} from 'lib/components';
import { useSelector, useDispatch } from 'lib/redux';
import { LOAD_CONSUMPTION_DATA, LOAD_SUBSCRIPTION_DASHBOARD } from 'constants/ActionTypes';
import { EVWholeHomeIcon } from '@eagleview/ev-comp-library';
import Typography from 'commonComponents/typography';
import SelectField from 'commonComponents/select-field';
import { useTranslation } from 'lib/translation';
import RetryScreen from 'commonComponents/retry-screen';
import HistoricUsage from './components/HistoricUsage';
import NonSubscriptionHistoricUsage from './components/NonSubscriptionHistoricUsage';
import ConsumptionPageLoader from './ConsumptionPageLoader.component';
import ConsumptionPageStyles from './ConsumptionPageStyles';
import {
  periodOptions, formatCurrency, calculateSubscriptionUsageData, formatDateForDisplay,
} from './Consumption.utils';

const useStyles = ConsumptionPageStyles;

function ConsumptionPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const { t } = useTranslation();

  const {
    subscriptionsData,
    subscriptionsLoading,
    consumptionData,
    consumptionLoading,
    consumptionError,
  } = useSelector(({ userData }) => ({
    subscriptionsData: userData?.subscriptionDashboard || { subscriptions: [], fees: {} },
    subscriptionsLoading: userData?.loading?.subscriptionDashboard || false,
    subscriptionsError: userData?.error?.subscriptionDashboard || null,
    consumptionData: userData?.consumption || {},
    consumptionLoading: userData?.loading?.consumption || false,
    consumptionError: userData?.error?.consumption || null,
  }));

  const subscriptions = subscriptionsData.subscriptions || [];
  const subscriptionTabs = subscriptions?.map((sub) => sub.subscriptionSkuName) || [];
  const allTabs = [...subscriptionTabs, t('consumption__non_subscription')];
  const currentSubscription = selectedTab < subscriptions.length ? subscriptions?.[selectedTab] : null;

  const loadConsumptionData = () => {
    if (!subscriptionsLoading && subscriptions.length === 0) {
      dispatch({ type: LOAD_SUBSCRIPTION_DASHBOARD });
    }
    if (selectedTab === subscriptions.length) {
      dispatch({ type: LOAD_CONSUMPTION_DATA });
    } else if (currentSubscription?.subscriptionItemId) {
      dispatch({ type: LOAD_CONSUMPTION_DATA, subscriptionId: currentSubscription.subscriptionItemId });
    }
  };

  useEffect(() => {
    loadConsumptionData();
  }, [currentSubscription?.subscriptionItemId, subscriptions.length, selectedTab, dispatch]);

  const transformNonSubscriptionHistoricUsage = (historicUsage, products) => {
    if (!historicUsage || !products) return [];

    // Keep product quantities separate for stacked chart display
    return historicUsage.map((item) => {
      const monthData = { month: item.month };

      // Add each product's quantity as a separate property
      products.forEach((product) => {
        const productId = String(product.id);
        monthData[productId] = item[productId] || 0;
      });

      return monthData;
    });
  };

  const getHistoricUsageData = () => {
    if (selectedTab === subscriptions.length && consumptionData?.historicUsage && consumptionData?.products) {
      return transformNonSubscriptionHistoricUsage(
        consumptionData.historicUsage,
        consumptionData.products,
      );
    }
    return consumptionData?.historicUsage || [];
  };

  const currentSubscriptionWithConsumption = currentSubscription ? {
    ...currentSubscription,
    historicUsage: getHistoricUsageData(),
    usageData: calculateSubscriptionUsageData(consumptionData?.historicUsage, currentSubscription),
  } : null;

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Show loading state for subscriptions
  if (subscriptionsLoading) {
    return (
      <EVGrid className={classes.pageContainer}>
        <EVSkeleton
          style={{
            height: '400px', width: '100%', borderRadius: '4px',
          }}
          animation="wave"
          variant="rect"
        />
      </EVGrid>
    );
  }

  // #genai Extracted nested ternary into independent render function
  const renderConsumptionContent = () => {
    if (consumptionLoading) {
      return <ConsumptionPageLoader />;
    }

    if (consumptionError) {
      return (
        <RetryScreen
          retryError={t('consumption__error_loading_consumption_data')}
          handleRetryButtonClick={loadConsumptionData}
          elevation={0}
        />
      );
    }

    // Non-Subscription tab
    if (selectedTab === subscriptions.length) {
      return (
        <>
          <EVGrid className={classes.subscriptionInfoContainer}>
            <EVGrid className={classes.filterContainer}>
              <SelectField
                selectId="period-select"
                selectedValue={selectedPeriod}
                handleChange={(e) => setSelectedPeriod(e.target.value)}
                selectOptions={periodOptions}
              />
            </EVGrid>
          </EVGrid>

          <EVGrid className={classes.contentSection}>
            <Typography variant="body-700" fontColor="charcoal-800">
              {t('subscription_dashboard__historic_usage')}
            </Typography>
            <EVGrid className={classes.usageSummaryContainer}>
              <EVGrid className={classes.chartSection}>
                <NonSubscriptionHistoricUsage
                  historicUsage={getHistoricUsageData()}
                  products={consumptionData?.products || []}
                />
              </EVGrid>
              <EVGrid className={classes.usageTableSection}>
                <Typography variant="body-600" fontColor="charcoal-800">
                  {t('consumption__non_subscription')}
                </Typography>
                {consumptionData?.products && consumptionData.products.length > 0 && (
                  <EVTableContainer>
                    <EVTable className={classes.usageTable}>
                      <EVTableBody>
                        {consumptionData.products.map((product) => {
                          const productId = String(product.id);
                          const totalQty = consumptionData?.historicUsage?.reduce((sum, month) => sum + (month[productId] || 0), 0) || 0;
                          // #genai API returns total cost in product.cost; do not multiply by quantity
                          return (
                            <EVTableRow key={product.id} className={classes.usageTableRow}>
                              <EVTableCell className={classes.usageTableLabel}>
                                {product.name}
                              </EVTableCell>
                              <EVTableCell className={classes.usageTableValue}>
                                <Typography variant="subtitle-500" fontColor="charcoal-800">
                                  {totalQty}
                                  {' '}
                                  (
                                  {formatCurrency(product.cost)}
                                  )
                                </Typography>
                              </EVTableCell>
                            </EVTableRow>
                          );
                        })}
                        <EVTableRow>
                          <EVTableCell>
                            <Typography variant="subtitle-600" fontColor="charcoal-900">
                              {t('consumption__total')}
                            </Typography>
                          </EVTableCell>
                          <EVTableCell className={classes.usageTableValue}>
                            <Typography variant="subtitle-500" fontColor="charcoal-800">
                              {formatCurrency(
                                // #genai product.cost is already total cost from API
                                consumptionData.products.reduce((totalSum, product) => totalSum + product.cost, 0),
                              )}
                            </Typography>
                          </EVTableCell>
                        </EVTableRow>
                      </EVTableBody>
                    </EVTable>
                  </EVTableContainer>
                )}
              </EVGrid>
            </EVGrid>
          </EVGrid>
        </>
      );
    }

    // Subscription tab
    if (currentSubscriptionWithConsumption) {
      return (
        <>
          <EVGrid className={classes.subscriptionInfoContainer}>
            <EVGrid className={classes.subscriptionIconContainer}>
              <EVWholeHomeIcon className={classes.iconStyle} />
              <EVGrid className={classes.subscriptionIcon}>
                <Typography className={classes.subscriptionIconText}>ONE</Typography>
              </EVGrid>
            </EVGrid>
            <EVGrid className={classes.subscriptionContentWrapper}>
              <EVGrid className={classes.subscriptionHeader}>
                <EVTypography className={classes.subscriptionTitle}>
                  {currentSubscriptionWithConsumption?.subscriptionSkuName || 'N/A'}
                </EVTypography>
              </EVGrid>
              <EVGrid className={classes.subscriptionDetails}>
                <EVGrid className={classes.subscriptionLeftColumn}>
                  <EVGrid className={classes.subscriptionDetailItem}>
                    <EVTypography className={classes.subscriptionDetailValue}>
                      {`${formatCurrency(currentSubscriptionWithConsumption?.monthlyFee)}/month`}
                    </EVTypography>
                  </EVGrid>
                  <EVGrid className={classes.subscriptionDetailItem}>
                    <EVTypography className={classes.subscriptionDetailValue}>
                      {`${currentSubscriptionWithConsumption?.includedQty} ${t('consumption__reports_per_month')}`}
                    </EVTypography>
                  </EVGrid>
                </EVGrid>
                <EVGrid className={classes.subscriptionRightColumn}>
                  <EVTableContainer>
                    <EVTable className={classes.subscriptionDetailsTable}>
                      <EVTableBody>
                        <EVTableRow>
                          <EVTableCell className={classes.subscriptionDetailLabel}>
                            {t('consumption__effective_date')}
                          </EVTableCell>
                          <EVTableCell className={classes.subscriptionDateValue}>
                            {formatDateForDisplay(currentSubscriptionWithConsumption.startDate)}
                          </EVTableCell>
                        </EVTableRow>
                        <EVTableRow>
                          <EVTableCell className={classes.subscriptionDetailLabel}>
                            {t('consumption__end_date')}
                          </EVTableCell>
                          <EVTableCell className={classes.subscriptionDateValue}>
                            {formatDateForDisplay(currentSubscriptionWithConsumption.endDate)}
                          </EVTableCell>
                        </EVTableRow>
                        <EVTableRow>
                          <EVTableCell className={classes.subscriptionDetailLabel}>
                            {t('consumption__billing_charge_overages')}
                          </EVTableCell>
                          <EVTableCell className={classes.subscriptionDateValue}>
                            {currentSubscriptionWithConsumption.overageRatePerUnit
                              ? formatCurrency(currentSubscriptionWithConsumption.overageRatePerUnit)
                              : (currentSubscriptionWithConsumption.overageRatePerUnit || 'N/A')}
                          </EVTableCell>
                        </EVTableRow>
                      </EVTableBody>
                    </EVTable>
                  </EVTableContainer>
                </EVGrid>
              </EVGrid>
            </EVGrid>

            <EVGrid className={classes.filterContainer}>
              <SelectField
                selectId="period-select"
                selectedValue={selectedPeriod}
                handleChange={(e) => setSelectedPeriod(e.target.value)}
                selectOptions={periodOptions}
              />
            </EVGrid>
          </EVGrid>

          <EVGrid className={classes.contentSection}>
            <Typography variant="body-700" fontColor="charcoal-800">
              {t('subscription_dashboard__historic_usage')}
            </Typography>
            <EVGrid className={classes.usageSummaryContainer}>
              <EVGrid className={classes.chartSection}>
                <HistoricUsage
                  historicUsage={currentSubscriptionWithConsumption.historicUsage}
                  totalAllowed={currentSubscriptionWithConsumption.includedQty || 0}
                />
              </EVGrid>
              <EVGrid className={classes.usageTableSection}>
                <Typography className={classes.usageTableTitle} variant="subtitle-600" fontColor="charcoal-800">
                  {t('consumption__usage_year_to_date')}
                </Typography>
                {currentSubscriptionWithConsumption.usageData && currentSubscriptionWithConsumption.usageData.length > 0 && (
                  <EVTableContainer>
                    <EVTable className={classes.usageTable}>
                      <EVTableBody>
                        {currentSubscriptionWithConsumption.usageData.map((row) => (
                          <EVTableRow key={row.label} className={classes.usageTableRow}>
                            <EVTableCell className={classes.usageTableLabel}>
                              {row.label}
                            </EVTableCell>
                            <EVTableCell className={classes.usageTableValue}>
                              {row.value}
                            </EVTableCell>
                          </EVTableRow>
                        ))}
                        <EVTableRow>
                          <EVTableCell className={classes.usageTableLabel}>
                            <Typography variant="subtitle-600" fontColor="charcoal-900">
                              {t('consumption__total_savings')}
                            </Typography>
                          </EVTableCell>
                          <EVTableCell className={classes.usageTableValue}>
                            {/* hardcoded for now */}
                            $1,000.00
                          </EVTableCell>
                        </EVTableRow>
                      </EVTableBody>
                    </EVTable>
                  </EVTableContainer>
                )}
              </EVGrid>
            </EVGrid>
          </EVGrid>
        </>
      );
    }

    return null;
  };

  return (
    <EVGrid className={classes.pageContainer}>
      <EVPaper className={classes.mainPaper}>
        <EVTabs
          value={selectedTab}
          onChange={handleTabChange}
          className={classes.tabsContainer}
          variant="fullWidth"
        >
          {allTabs.map((tab) => (
            <EVTab key={tab} label={tab} className={classes.tab} />
          ))}
        </EVTabs>

        {renderConsumptionContent()}
      </EVPaper>
    </EVGrid>
  );
}

export default ConsumptionPage;
